import React, { useState, useCallback } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { RotateCcw, History, AlertTriangle, Loader2, X, Undo2 } from "lucide-react";

const norm = (s) => (s || "").replace(/\s+/g, " ").trim().toLowerCase();

/* Dir 84 — Deep-reset confirm dialog + recoverable undo/restore + auditable history.
   The typed bid name is sent as confirm_name to the SERVER-SIDE guard (Dir 83); this is never a
   client-only gate — a bare/mismatched attempt still surfaces the guard's 409 in-modal. */
export function ResetControl({ deal, reload }) {
  const [open, setOpen] = useState(false);
  const [preview, setPreview] = useState(null);
  const [typed, setTyped] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [showHist, setShowHist] = useState(false);
  const [hist, setHist] = useState(null);

  const expected = preview?.confirm_name_expected || deal.title || deal.id;
  const matches = typed.length > 0 && norm(typed) === norm(expected);

  const openDialog = async () => {
    setErr(""); setTyped(""); setPreview(null); setOpen(true);
    try {
      const { data } = await api.get(`/bids/${deal.id}/reset-preview?action=deep-reset`);
      setPreview(data);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Could not load reset preview.");
    }
  };

  const doReset = async () => {
    setBusy(true); setErr("");
    try {
      await api.post(`/bids/${deal.id}/engine/reset?deep=true`, { confirm: true, confirm_name: typed });
      toast.success("Deep-reset complete — a recoverable snapshot was saved. Use “Undo last reset” to revert.");
      setOpen(false); reload?.(); loadHistory();
    } catch (e) {
      const d = e?.response?.data?.detail;
      setErr((d && (d.message || d)) || "Reset refused.");
    } finally { setBusy(false); }
  };

  const loadHistory = useCallback(async () => {
    try { const { data } = await api.get(`/bids/${deal.id}/reset-history`); setHist(data); }
    catch { setHist({ count: 0, history: [] }); }
  }, [deal.id]);

  const toggleHist = () => {
    const next = !showHist; setShowHist(next);
    if (next && !hist) loadHistory();
  };

  const restore = async (snapId) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/bids/${deal.id}/reset/restore`, snapId ? { snap_id: snapId } : {});
      toast.success(`Restored ${data.restored_fields?.length || 0} field(s) from snapshot — undo complete.`);
      reload?.(); loadHistory();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Nothing to restore.");
    } finally { setBusy(false); }
  };

  return (
    <>
      <button data-testid="reset-control-open-btn" onClick={openDialog}
        className="flex items-center gap-2 bg-red-50 border border-red-300 text-red-700 text-sm font-semibold px-4 py-2 rounded-md hover:bg-red-100">
        <RotateCcw className="h-4 w-4" /> Deep reset
      </button>
      <button data-testid="reset-undo-btn" onClick={() => restore(null)} disabled={busy}
        title="Restore this bid from the most recent reset snapshot"
        className="flex items-center gap-2 bg-white border border-zinc-300 text-zinc-700 text-sm font-semibold px-4 py-2 rounded-md hover:bg-zinc-50 disabled:opacity-50">
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Undo2 className="h-4 w-4 text-emerald-600" />} Undo last reset
      </button>
      <button data-testid="reset-history-toggle" onClick={toggleHist}
        className="flex items-center gap-2 bg-white border border-zinc-300 text-zinc-600 text-sm font-semibold px-3 py-2 rounded-md hover:bg-zinc-50">
        <History className="h-4 w-4" /> Reset history
      </button>

      {showHist && (
        <div data-testid="reset-history-list" className="w-full mt-2 bg-zinc-50 border border-zinc-200 rounded-lg p-3">
          {!hist ? (
            <div className="text-xs text-zinc-500 flex items-center gap-2"><Loader2 className="h-3 w-3 animate-spin" /> Loading…</div>
          ) : hist.count === 0 ? (
            <div className="text-xs text-zinc-500">No resets recorded for this bid.</div>
          ) : (
            <ul className="space-y-2">
              {hist.history.map((h) => (
                <li key={h.id} className="flex items-start justify-between gap-3 text-xs border-b border-zinc-200 pb-2 last:border-0">
                  <div className="min-w-0">
                    <div className="font-semibold text-zinc-800">{h.action}</div>
                    <div className="text-zinc-500">{new Date(h.at).toLocaleString()} · by {h.by || "—"}</div>
                    <div className="text-zinc-400 mt-0.5">Discarded: {(h.discarded || []).join("; ") || "—"}</div>
                  </div>
                  <button data-testid={`reset-history-restore-${h.id}`} onClick={() => restore(h.id)} disabled={busy}
                    className="shrink-0 flex items-center gap-1 text-[11px] bg-white border border-zinc-300 rounded px-2 py-1 hover:bg-zinc-50 disabled:opacity-50">
                    <Undo2 className="h-3 w-3 text-emerald-600" /> Restore
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" data-testid="reset-confirm-modal">
          <div className="bg-white rounded-xl shadow-xl max-w-lg w-full p-5">
            <div className="flex items-start justify-between mb-3">
              <div className="flex items-center gap-2 text-red-700">
                <AlertTriangle className="h-5 w-5" />
                <h3 className="font-display font-bold text-lg">Confirm deep reset</h3>
              </div>
              <button data-testid="reset-confirm-cancel-btn" onClick={() => setOpen(false)} className="text-zinc-400 hover:text-zinc-700"><X className="h-5 w-5" /></button>
            </div>

            {!preview && !err && (
              <div className="text-sm text-zinc-500 flex items-center gap-2 py-4"><Loader2 className="h-4 w-4 animate-spin" /> Loading what will be lost…</div>
            )}

            {preview && (
              <>
                <p data-testid="reset-loss-statement" className="text-sm text-zinc-700 leading-relaxed bg-amber-50 border border-amber-200 rounded-lg p-3">
                  {preview.confirmation_statement}
                </p>
                <p className="text-[11px] text-zinc-500 mt-2">
                  This is recoverable — a full snapshot is saved and can be restored with “Undo last reset”.
                </p>
                <label className="block text-xs font-semibold text-zinc-600 mt-4 mb-1">
                  Type the bid name to confirm: <span className="font-mono text-zinc-900">{expected}</span>
                </label>
                <input data-testid="reset-confirm-name-input" value={typed} onChange={(e) => setTyped(e.target.value)}
                  placeholder={expected} autoFocus
                  className="w-full border border-zinc-300 rounded-md px-3 py-2 text-sm outline-none focus:border-red-400" />
              </>
            )}

            {err && (
              <div data-testid="reset-confirm-error" className="mt-3 text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg p-2.5">{err}</div>
            )}

            <div className="flex justify-end gap-2 mt-5">
              <button onClick={() => setOpen(false)} className="text-sm px-4 py-2 rounded-md border border-zinc-300 text-zinc-600 hover:bg-zinc-50">Cancel</button>
              <button data-testid="reset-confirm-submit-btn" onClick={doReset} disabled={!matches || busy}
                title={matches ? "" : "Type the exact bid name to enable"}
                className="text-sm px-4 py-2 rounded-md bg-red-600 text-white font-semibold hover:bg-red-500 disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-2">
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <RotateCcw className="h-4 w-4" />} Deep reset
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
